import { FC, ReactNode } from 'react';
import { cn } from '@wwe-portal/ui/lib/lib';
import { PlayerRank } from '@wwe-portal/entities/rbe.type';
import { RankCell } from '../../../../../packages/ui/features/leaderboard/leaderboard-row';
import { PlayerAvatar } from '../common/image/player-avatar';
import Image from 'next/image';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { formatThousands } from '@wwe-portal/utils/format';
import styles from './solo-event-leaderboard-row.module.css';
import {
  Skeleton,
  TextSkeleton,
} from '../../../../../packages/ui/components/skeleton';

export const SoloEventLeaderboardRowLayout: FC<{
  rank: ReactNode;
  name: ReactNode;
  points: ReactNode;
  className?: string;
}> = ({ rank, name, points, className }) => {
  return (
    <div
      className={cn('flex items-center justify-between px-4 py-2.5', className)}
    >
      <div className="flex items-center gap-2">
        <span className="mr-3 w-8">{rank}</span>
        <span>{name}</span>
      </div>
      <div>{points}</div>
    </div>
  );
};

export const SoloEventLeaderboardRow: FC<{
  player: PlayerRank;
  isCurrentPlayer: boolean;
  currentPlayerRow?: PlayerRank;
  className?: string;
}> = ({ player, isCurrentPlayer, currentPlayerRow, className }) => {
  const currentUser = useGetCurrentUser();
  const canShowPlayerAvatar =
    (player.userId && !player.isHidden) || isCurrentPlayer;
  return (
    <SoloEventLeaderboardRowLayout
      key={player.playerRank}
      className={cn(className, {
        [styles.currentPlayerRow ?? '']: isCurrentPlayer,
      })}
      rank={
        <div className="relative">
          {isCurrentPlayer && (
            <>
              <span className={styles.yourRankTag}>Your Rank</span>
              <span className={styles.rightAngle} />
            </>
          )}
          {player.playerRank != null ? (
            <RankCell rank={player.playerRank} highlight={isCurrentPlayer} />
          ) : null}
        </div>
      }
      name={
        <div className="flex items-center gap-3 text-xs">
          <div
            className={cn('relative size-[30px] overflow-hidden rounded-full', {
              'size-[22px]': isCurrentPlayer,
            })}
          >
            {canShowPlayerAvatar ? (
              <>
                <PlayerAvatar
                  className={cn('h-[30px] w-[30px]', {
                    'h-[22px] w-[22px]': isCurrentPlayer,
                  })}
                  userId={player.userId}
                  userName={player.playerName}
                />
                <div
                  className={cn('absolute inset-0 rounded-full', {
                    'border-blue600 border border-solid': player.userId != null,
                  })}
                />
              </>
            ) : (
              <div className="bg-text600 relative flex h-[30px] w-[30px] items-center justify-center">
                <Image
                  src="/assets/images/common/default-avatar.svg"
                  alt="Default avatar"
                  width={18}
                  height={18}
                  className="object-cover"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center">
            {isCurrentPlayer
              ? currentPlayerRow?.playerName || currentUser?.nickname
              : player?.playerTemporaryName || player.playerName || ''}
          </div>
        </div>
      }
      points={
        <span
          className={cn({
            'text-yellow700 text-lg': isCurrentPlayer,
          })}
        >
          {formatThousands(player.playerPoints)}
        </span>
      }
    />
  );
};

export const SoloEventLeaderboardRowSkeleton: FC = () => {
  return (
    <SoloEventLeaderboardRowLayout
      rank={<Skeleton className="h-[30px] w-[30px] rounded-full" />}
      name={<TextSkeleton lineHeight={16} fontSize={12} className="w-20" />}
      points={<TextSkeleton lineHeight={16} fontSize={12} className="w-8" />}
    />
  );
};
